import Vue from 'vue';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirebaseAuth } from '../firebase/firebase';
import {
  firestoreDocumentById,
  onSnapshot,
  executeQuery,
  firestoreCollection,
} from '../firebase/firestore';

const MODULE_PREFIX = 'user';
const GETTER_USER_DETAILS = 'userDetails';
const GETTER_USER_ID = 'userId';
const GETTER_USER_IS_ORG_ADMIN = 'userIsOrgAdmin';
const GETTER_USER_READY = 'userReady';
const GETTER_COLLEAGUES = 'colleagues';
const GETTER_GROUPS = 'groups';
const ACTION_FETCH_USER_DETAILS = 'fetchUserDetails';
const MUTATION_USER_DETAILS = 'MUTATION_USER_DETAILS';
const MUTATION_USER_READY = 'MUTATION_USER_READY';
const MUTATION_FETCHED_COLLEAGUES = 'FETCHED_COLLEAGUES';
const ACTION_FETCH_COLLEAGUES = 'fetchColleagues';

const initialState = {
  userDetails: {},
  userReady: false,
  colleagues: [],
};

const createUserDetail = (snapshot, rolesSnapshot) => {
  const user = snapshot.data();
  const roles = rolesSnapshot.map(role => role.data().role);
  return {
    userId: snapshot.id,
    organizationId: user.organizationId,
    displayName: `${user.firstName} ${user.lastName}`,
    featureSwitches: user.featureSwitches,
    groups: user.groups,
    roles,
  };
};

const onUserDetailsSnapshot = ({ commit, rolesSnapshot }, snapshot) => {
  commit(MUTATION_USER_DETAILS, createUserDetail(snapshot, rolesSnapshot));
};

let unsubscribeUserStatus;
let unsubscribeUserDetails;

const actions = {
  [ACTION_FETCH_USER_DETAILS]: async ({ commit, dispatch }) => {
    if (unsubscribeUserDetails) unsubscribeUserDetails();
    if (unsubscribeUserStatus) unsubscribeUserStatus();

    await new Promise((resolve, reject) => {
      unsubscribeUserStatus = onAuthStateChanged(getFirebaseAuth(), async (user) => {
        try {
          if (user) {
            const userId = user.uid;
            const docRef = firestoreDocumentById('users', userId);
            const snapshot = await docRef.get();
            let rolesSnapshot = [];
            try {
              rolesSnapshot = await Promise.all(snapshot.data().roles.map(ref => ref.get()));
            } catch {
              // swallow
            }

            commit(MUTATION_USER_DETAILS, createUserDetail(snapshot, rolesSnapshot));
            commit(MUTATION_USER_READY, true);

            if (unsubscribeUserDetails) {
              unsubscribeUserDetails();
            }

            unsubscribeUserDetails = onSnapshot(docRef, onUserDetailsSnapshot.bind(this, { commit, rolesSnapshot }));
            dispatch(types.actions.fetchColleagues, undefined, { root: true });
            resolve();
          } else {
            reject();
          }
        } catch (error) {
          reject(error);
        }
      });
    });
    unsubscribeUserStatus();
  },
  [ACTION_FETCH_COLLEAGUES]: async ({ rootGetters, commit }) => {
    const userDetails = rootGetters[types.getters.userDetails];
    try {
      const groupUsers = await Promise.all(userDetails.groups.map(group => {
        const collection = `organizations/${userDetails.organizationId}/groups/${group.id}/groupUsers`;
        return executeQuery(firestoreCollection(collection));
      }));
      const colleaguesById = new Map();

      groupUsers
        .flat()
        .filter(groupUser => groupUser.id !== userDetails.userId)
        .map(groupUser => ({
          groupId: groupUser.groupId,
          id: groupUser.id,
          firstName: groupUser.firstName,
          lastName: groupUser.lastName,
          email: groupUser.email,
        }))
        .forEach(colleague => colleaguesById.set(colleague.id, colleague));

      await Promise.all([...colleaguesById.values()].map(async (colleague) => {
        const userSnapshot = await firestoreDocumentById('users', colleague.id).get();
        if (!userSnapshot.exists) {
          colleaguesById.delete(colleague.id);
        }
      }));

      const colleagues = [...colleaguesById.values()];

      colleagues.sort((first, second) =>
        `${first.lastName} ${first.first}`
          .localeCompare(`${second.lastName} ${second.first}`, 'en', { sensitivity: 'base' }));

      commit(MUTATION_FETCHED_COLLEAGUES, colleagues);
    } catch (error) {
      console.error('ERROR ACTION_FETCH_COLLEAGUES', error);
    }
  },
};

const mutations = {
  [MUTATION_USER_DETAILS]: (state, userDetails) => {
    Vue.set(state, 'userDetails', userDetails);
  },
  [MUTATION_USER_READY]: (state, userReady) => {
    Vue.set(state, 'userReady', userReady);
  },
  [MUTATION_FETCHED_COLLEAGUES]: (state, colleagues) => {
    Vue.set(state, 'colleagues', colleagues);
  },
};

const getters = {
  [GETTER_USER_DETAILS]: state => state.userDetails,
  [GETTER_USER_ID]: state => state.userDetails.userId,
  [GETTER_USER_READY]: state => state.userReady,
  [GETTER_COLLEAGUES]: state => state.colleagues || [],
  [GETTER_GROUPS]: state => state.userDetails.groups || [],
  [GETTER_USER_IS_ORG_ADMIN]: state => {
    if (!state.userDetails) return false;
    if (!state.userDetails.roles) return false;
    return state.userDetails.roles.includes('NOOS_ADMIN');
  },
};

export default {
  namespaced: true, state: initialState, getters, mutations, actions,
};

export const types = {
  getters: {
    userDetails: `${MODULE_PREFIX}/${GETTER_USER_DETAILS}`,
    userId: `${MODULE_PREFIX}/${GETTER_USER_ID}`,
    userIsOrgAdmin: `${MODULE_PREFIX}/${GETTER_USER_IS_ORG_ADMIN}`,
    userReady: `${MODULE_PREFIX}/${GETTER_USER_READY}`,
    colleagues: `${MODULE_PREFIX}/${GETTER_COLLEAGUES}`,
    groups: `${MODULE_PREFIX}/${GETTER_GROUPS}`,
  },
  mutations: {
    USER_DETAILS: `${MODULE_PREFIX}/${MUTATION_USER_DETAILS}`,
    USER_READY: `${MODULE_PREFIX}/${MUTATION_USER_READY}`,
    FETCHED_COLLEAGUES: `${MODULE_PREFIX}/${MUTATION_FETCHED_COLLEAGUES}`,
  },
  actions: {
    fetchUserDetails: `${MODULE_PREFIX}/${ACTION_FETCH_USER_DETAILS}`,
    fetchColleagues: `${MODULE_PREFIX}/${ACTION_FETCH_COLLEAGUES}`,
  },
};
